import {
	REGISTRATION_INTRO_ACTIVE_CLASS,
	REGISTRATION_INTRO_PREVIEW_KEY,
	getRegistrationIntroPreviewDirective,
	resolveRegistrationIntroTrigger,
	type RegistrationIntroTrigger
} from './registrationIntroPolicy';

export type RegistrationIntroRuntimePorts = {
	getSearch: () => string;
	getPathname: () => string;
	getSessionItem: (key: string) => string | null;
	setSessionItem: (key: string, value: string) => void;
	removeSessionItem: (key: string) => void;
	addRootClass: (className: string) => void;
	removeRootClass: (className: string) => void;
};

const createBrowserPorts = (): RegistrationIntroRuntimePorts | null => {
	if (typeof window === 'undefined' || typeof document === 'undefined') return null;
	const getSessionItem = (key: string) => {
		try {
			return window.sessionStorage.getItem(key);
		} catch {
			return null;
		}
	};
	const setSessionItem = (key: string, value: string) => {
		try {
			window.sessionStorage.setItem(key, value);
		} catch {
			// The overlay still runs for the current document when storage is unavailable.
		}
	};
	const removeSessionItem = (key: string) => {
		try {
			window.sessionStorage.removeItem(key);
		} catch {
			// Nothing remains to clear when storage is unavailable.
		}
	};

	return {
		getSearch: () => window.location.search,
		getPathname: () => window.location.pathname,
		getSessionItem,
		setSessionItem,
		removeSessionItem,
		addRootClass: (className) => document.documentElement.classList.add(className),
		removeRootClass: (className) => document.documentElement.classList.remove(className)
	};
};

export const getRegistrationIntroTrigger = (
	{
		authenticated,
		onboardingIncomplete
	}: {
		authenticated: boolean;
		onboardingIncomplete: boolean;
	},
	ports: RegistrationIntroRuntimePorts | null = createBrowserPorts()
): RegistrationIntroTrigger => {
	if (!ports) return 'none';

	const search = ports.getSearch();
	const previewDirective = getRegistrationIntroPreviewDirective(search);
	if (previewDirective === 'enable') ports.setSessionItem(REGISTRATION_INTRO_PREVIEW_KEY, '1');
	if (previewDirective === 'disable') ports.removeSessionItem(REGISTRATION_INTRO_PREVIEW_KEY);

	const trigger = resolveRegistrationIntroTrigger({
		search,
		pathname: ports.getPathname(),
		authenticated,
		onboardingIncomplete,
		previewSession: ports.getSessionItem(REGISTRATION_INTRO_PREVIEW_KEY) === '1'
	});

	if (trigger !== 'none') ports.addRootClass(REGISTRATION_INTRO_ACTIVE_CLASS);
	else if (ports.getPathname() !== '/onboarding') {
		ports.removeRootClass(REGISTRATION_INTRO_ACTIVE_CLASS);
	}
	return trigger;
};

export const releaseRegistrationIntroCover = (
	ports: RegistrationIntroRuntimePorts | null = createBrowserPorts()
) => {
	ports?.removeRootClass(REGISTRATION_INTRO_ACTIVE_CLASS);
};

export const holdRegistrationIntroCover = (
	ports: RegistrationIntroRuntimePorts | null = createBrowserPorts()
) => {
	ports?.addRootClass(REGISTRATION_INTRO_ACTIVE_CLASS);
};

export const completeRegistrationIntro = (
	_trigger: RegistrationIntroTrigger,
	ports: RegistrationIntroRuntimePorts | null = createBrowserPorts()
) => {
	if (!ports) return;
	ports.removeRootClass(REGISTRATION_INTRO_ACTIVE_CLASS);
};
